import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import '../styles/MusicPlayer.css';

const MusicPlayer = () => {
  const { t } = useTranslation();
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [playlist, setPlaylist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repeat, setRepeat] = useState('off');
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const audioRef = useRef(null);

  // Last.fm API Key
  const LASTFM_API_KEY = '5b32e53deac280810209dc3f536492d0';
  const LASTFM_API_URL = 'https://ws.audioscrobbler.com/2.0/';

  // Initial popular tracks with real URLs
  // const defaultTracks = [
  //   { 
  //     title: 'Blinding Lights', 
  //     artist: 'The Weeknd', 
  //     duration: 200,
  //     url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3'
  //   },
  //   { 
  //     title: 'Shape of You', 
  //     artist: 'Ed Sheeran', 
  //     duration: 234,
  //     url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3'
  //   },
  //   { 
  //     title: 'Someone You Loved', 
  //     artist: 'Lewis Capaldi', 
  //     duration: 182,
  //     url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3'
  //   },
  //   { 
  //     title: 'Levitating', 
  //     artist: 'Dua Lipa', 
  //     duration: 203,
  //     url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3'
  //   },
  //   { 
  //     title: 'As It Was', 
  //     artist: 'Harry Styles', 
  //     duration: 169,
  //     url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3'
  //   },
  // ];

  // ⭐ DEFINE currentTrack BEFORE useEffects
  const currentTrack = playlist[currentTrackIndex];

  // Load initial playlist - EMPTY for each user
  useEffect(() => {
    // Load playlist from localStorage if exists
    const savedPlaylist = localStorage.getItem('musicPlaylist');
    if (savedPlaylist) {
      try {
        setPlaylist(JSON.parse(savedPlaylist));
      } catch (err) {
        console.error('Error loading playlist:', err);
        setPlaylist([]);
      }
    } else {
      setPlaylist([]);
    }
    setLoading(false);
  }, []);

  // Save playlist to localStorage whenever it changes
  useEffect(() => {
    if (playlist.length > 0) {
      localStorage.setItem('musicPlaylist', JSON.stringify(playlist));
    }
  }, [playlist]);

  // Auto play when track changes
  useEffect(() => {
    if (audioRef.current && currentTrack?.url) {
      audioRef.current.src = currentTrack.url; // Update src when track changes
      if (isPlaying) {
        setTimeout(() => {
          audioRef.current?.play().catch(err => console.log('Auto-play blocked:', err));
        }, 100);
      }
    }
  }, [currentTrackIndex, currentTrack?.url, isPlaying]);

  // Search in Last.fm
  const searchLastFm = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(LASTFM_API_URL, {
        params: {
          method: 'track.search',
          track: query,
          api_key: LASTFM_API_KEY,
          format: 'json',
          limit: 10,
        },
      });

      const tracks = response.data.results?.trackmatches?.track || [];
      // Generate playable music URLs (use demo URLs)
      const demoUrls = [
        'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3',
        'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3',
        'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3',
        'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3',
        'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3',
      ];
      
      const formattedTracks = tracks.map((track, idx) => ({
        id: idx,
        title: track.name,
        artist: track.artist,
        duration: Math.floor(Math.random() * 300) + 60, // Random duration
        url: demoUrls[idx % demoUrls.length], // Use demo URL for playback
      }));

      setSearchResults(formattedTracks);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchLastFm(searchQuery);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handlePlayPause = () => {
    if (!audioRef.current || !currentTrack) return;
    
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Ensure audio source is set
        if (!audioRef.current.src || audioRef.current.src !== currentTrack.url) {
          audioRef.current.src = currentTrack.url;
        }
        
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true);
            })
            .catch(err => {
              console.error('Play error:', err);
              setIsPlaying(false);
            });
        }
      }
    } catch (err) {
      console.error('Play/Pause error:', err);
    }
  };

  const handleNextTrack = () => {
    if (currentTrackIndex < playlist.length - 1) {
      setCurrentTrackIndex(currentTrackIndex + 1);
      setCurrentTime(0);
      setIsPlaying(true);
    } else if (repeat === 'all') {
      setCurrentTrackIndex(0);
      setCurrentTime(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  const handlePrevTrack = () => {
    if (currentTrackIndex > 0) {
      setCurrentTrackIndex(currentTrackIndex - 1);
      setCurrentTime(0);
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = (e) => {
    setCurrentTime(e.target.currentTime);
  };

  const handleLoadedMetadata = (e) => {
    setDuration(e.target.duration);
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleRepeat = () => {
    const states = ['off', 'one', 'all'];
    const currentIndex = states.indexOf(repeat);
    setRepeat(states[(currentIndex + 1) % states.length]);
  };

  const handleEnded = () => {
    if (repeat === 'one') {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(err => console.log('Play error:', err));
      }
    } else {
      handleNextTrack();
    }
  };

  const addTrackToPlaylist = (track) => {
    setPlaylist([...playlist, track]);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (loading) {
    return <div className="music-player">{t('music.loading')}</div>;
  }

  if (playlist.length === 0) {
    return (
      <div className="music-player">
        <div className="player-container">
          <div className="now-playing-info">
            <div className="album-art">🎵</div>
            <div className="track-info">
              <div className="track-title">{t('music.noTracks')}</div>
              <div className="track-artist">{t('music.searchAdd')}</div>
            </div>
          </div>
          
          <div className="search-section">
            <button
              className="search-toggle-btn"
              onClick={() => setShowSearch(!showSearch)}
              title={t('music.searchTracks')}
            >
              🔍
            </button>
            {showSearch && (
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('music.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searching && <div className="search-loading">{t('music.searching')}</div>}
                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map((track) => (
                      <div
                        key={`${track.title}-${track.artist}`}
                        className="search-result-item"
                        onClick={() => addTrackToPlaylist(track)}
                      >
                        <div className="result-title">{track.title}</div>
                        <div className="result-artist">{track.artist}</div>
                        <button className="add-btn">{t('music.addButton')}</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="music-player">
      <audio
        ref={audioRef}
        src={currentTrack?.url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        crossOrigin="anonymous"
      />

      <div className="player-container">
        {/* Now Playing Info */}
        <div className="now-playing-info">
          <div className="album-art">🎵</div>
          <div className="track-info">
            <div className="track-title">{currentTrack.title}</div>
            <div className="track-artist">{currentTrack.artist}</div>
            <div className="track-counter">
              {currentTrackIndex + 1} / {playlist.length}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="progress-container">
          <span className="time">{formatTime(currentTime)}</span>
          <input
            type="range"
            className="progress-slider"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
          />
          <span className="time">{formatTime(duration)}</span>
        </div>

        {/* Controls */}
        <div className="player-controls">
          <button
            className={`control-btn repeat-btn ${repeat !== 'off' ? 'active' : ''}`}
            onClick={handleRepeat}
            title={t('music.repeat')}
          >
            {repeat === 'off' && '🔄'}
            {repeat === 'one' && '🔂'}
            {repeat === 'all' && '🔁'}
          </button>

          <button
            className="control-btn prev-btn"
            onClick={handlePrevTrack}
            disabled={currentTrackIndex === 0}
            title={t('music.previous')}
          >
            ⏮️
          </button>

          <button
            className={`control-btn play-btn ${isPlaying ? 'playing' : ''}`}
            onClick={handlePlayPause}
            title={isPlaying ? t('music.pause') : t('music.play')}
          >
            {isPlaying ? '⏸️' : '▶️'}
          </button>

          <button
            className="control-btn next-btn"
            onClick={handleNextTrack}
            disabled={currentTrackIndex === playlist.length - 1 && repeat === 'off'}
            title={t('music.next')}
          >
            ⏭️
          </button>

          <div className="volume-control">
            <span className="volume-icon">🔊</span>
            <input
              type="range"
              className="volume-slider"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              title="Volume"
            />
          </div>
        </div>

        {/* Search Section */}
        <div className="search-section">
          <button
            className="search-toggle-btn"
            onClick={() => setShowSearch(!showSearch)}
            title="Search tracks"
          >
            🔍
          </button>
          {showSearch && (
            <div className="search-box">
              <input
                type="text"
                placeholder="Search tracks on Last.fm..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searching && <div className="search-loading">Searching...</div>}
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((track) => (
                    <div
                      key={`${track.title}-${track.artist}`}
                      className="search-result-item"
                      onClick={() => addTrackToPlaylist(track)}
                    >
                      <div className="result-title">{track.title}</div>
                      <div className="result-artist">{track.artist}</div>
                      <button className="add-btn">+ Add</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Playlist */}
        <div className="playlist-section">
          <div className="playlist-header">
            <h3>🎵 Playlist ({playlist.length})</h3>
          </div>
          <div className="playlist-items">
            {playlist.map((track, index) => (
              <div
                key={`${index}-${track.title}`}
                className={`playlist-item ${index === currentTrackIndex ? 'active' : ''}`}
                onClick={() => {
                  setCurrentTrackIndex(index);
                  setCurrentTime(0);
                  setIsPlaying(true);
                }}
              >
                <span className="track-number">
                  {index === currentTrackIndex && isPlaying ? '▶️' : index + 1}
                </span>
                <div className="item-info">
                  <div className="item-title">{track.title}</div>
                  <div className="item-artist">{track.artist}</div>
                </div>
                <span className="item-duration">{formatTime(track.duration)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MusicPlayer;
